import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Canvas, useFrame, extend } from '@react-three/fiber';
import { Stars, OrbitControls, Ring, Line as DreiLine, Html, Sparkles, Cloud, Float, Environment } from '@react-three/drei';
import { useSpring, a } from '@react-spring/three';
import { EffectComposer, Bloom, Vignette, Noise, ChromaticAberration } from '@react-three/postprocessing';
import * as THREE from 'three';
import * as LucideIcons from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useAppContext } from '../AppContext';
import UserHoverCard from './UserHoverCard';
import ConstellationSettingsModal from './ConstellationSettingsModal';
import BlackHole from './BlackHole';





const ShootingStar = () => {
    const ref = useRef();
    const [active, setActive] = useState(false);

    useFrame((state, delta) => {
        if (active && ref.current) {
            ref.current.position.x -= 800 * delta; 
            ref.current.position.y -= 90 * delta; 
            
            if (ref.current.position.x < -200) setActive(false); 
        } else if (!active && Math.random() < 0.01) { 
            setActive(true);
            const yStart = -20 + Math.random() * 60;
            const zStart = -100 + Math.random() * 120;
            ref.current.position.set(200, yStart, zStart); 
            
            const angle = Math.atan2(-90, -800) - Math.PI / 2;
            ref.current.rotation.z = angle; 
        }
    });

    return (
        <mesh ref={ref} visible={active}>
            <cylinderGeometry args={[0, 0.08, 12, 8]} /> 
            <meshBasicMaterial 
                color={new THREE.Color(0.5, 0.8, 1).multiplyScalar(5)} 
                transparent 
                opacity={0.8} 
                blending={THREE.AdditiveBlending} 
                toneMapped={false} 
            />
        </mesh>
    );
};

const StormFlash = () => {
    const lightRef = useRef();
    useFrame(() => {
        if (Math.random() < 0.08) { 
            lightRef.current.intensity = 20 + Math.random() * 40;
            lightRef.current.position.set(
                (Math.random() - 0.5) * 40,
                (Math.random() - 0.5) * 20,
                5
            );
        } else {
            lightRef.current.intensity = THREE.MathUtils.lerp(lightRef.current.intensity, 0, 0.2);
        }
    });
    return <pointLight ref={lightRef} color="#e0f2fe" distance={60} decay={1} />;
};

const NebulaGlow = ({ position, color, size }) => {
    const ref = useRef();
    useFrame((state) => {
        const t = state.clock.getElapsedTime();
        if (ref.current) {
            const scale = 1 + Math.sin(t * 0.3 + position[0]) * 0.1;
            ref.current.scale.setScalar(scale);
        }
    });
    return (
        <mesh ref={ref} position={position}>
            <sphereGeometry args={[size, 32, 32]} />
            <meshBasicMaterial color={color} transparent opacity={0.06} blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
        </mesh>
    );
};

const Moonlet = ({ radius, speed, angleOffset, scale = 1 }) => {
    const ref = useRef();
    useFrame((state) => {
        const t = state.clock.getElapsedTime() * speed;
        ref.current.position.x = (radius * scale) * Math.cos(t + angleOffset);
        ref.current.position.z = (radius * scale) * Math.sin(t + angleOffset);
    });
    return (
        <mesh ref={ref}>
            <sphereGeometry args={[0.06 * scale, 16, 16]} />
            <meshStandardMaterial 
                emissive="#ffffff" 
                emissiveIntensity={4} 
                toneMapped={false} 
                color="#ffffff"
            />
            <pointLight distance={0.5} intensity={2} color="white" />
        </mesh>
    );
};

const CrystallineRing = ({ radius, speed, rotation, color }) => {
    const ref = useRef();
    useFrame(() => {
        if (ref.current) ref.current.rotation.z += speed;
    });
    return (
        <group rotation={rotation}>
            <Ring ref={ref} args={[radius, radius + 0.02, 64]}>
                <meshPhysicalMaterial 
                    color={color} 
                    emissive={color} 
                    emissiveIntensity={10} 
                    toneMapped={false} 
                    transparent 
                    opacity={0.6}
                    roughness={0}
                    metalness={1}
                    side={THREE.DoubleSide}
                />
            </Ring>
        </group>
    );
};

const CosmicConnection = ({ startRef, endRef, level, isSupernova }) => {
    const [points, setPoints] = useState([new THREE.Vector3(0, 0, 0), new THREE.Vector3(0, 0, 0)]);
    const startVec = useMemo(() => new THREE.Vector3(), []);
    const endVec = useMemo(() => new THREE.Vector3(), []);
    const [lineOpacity, setLineOpacity] = useState(0.15); 

    useFrame((state) => {
        if (startRef.current && endRef.current) {
            startRef.current.getWorldPosition(startVec);
            endRef.current.getWorldPosition(endVec);
            setPoints([startVec.clone(), endVec.clone()]);
        }
        setLineOpacity(0.15 + Math.sin(state.clock.getElapsedTime() * 2) * 0.05); 
    });

    return (
        <DreiLine 
            points={points} 
            lineWidth={1} 
            color="#a5b4fc" 
            transparent 
            opacity={lineOpacity} 
        />
    );
};

const UserProfileCard = ({ profile, onViewProfile }) => {
    const { t } = useTranslation();
    return (
        <Html position={[0, 1.2, 0]} center>
            <div className="selected-star-card">
                <img src={profile.photoURL || "https://placehold.co/40x40/AEC6CF/FFFFFF?text=U"} alt={profile.displayName} className="w-12 h-12 rounded-full object-cover mx-auto mb-2 border-2 border-purple-400" />
                <p className="font-bold text-white text-sm truncate">{profile.displayName}</p>
                <button
                    onClick={(e) => { e.stopPropagation(); onViewProfile(profile.id); }}
                    className="flex items-center justify-center gap-2 mt-3 text-xs px-3 py-1 bg-blue-600/50 text-white rounded-full hover:bg-blue-500/80 transition-colors"
                >
                    <LucideIcons.User size={12} />
                    {t('interactiveConstellation.viewProfile')}
                </button>
            </div>
        </Html>
    );
};

const Star = React.forwardRef(({ position, userData, onSelect, onHover, isCenterStar, isSelected }, ref) => {
    const { handleUserSelect } = useAppContext();
    const [isHovered, setIsHovered] = useState(false);

    const level = userData?.constellationLevel || 1;
    const sizeBonus = level >= 66 ? 0.75 : level >= 26 ? 0.3 : 0;
    const finalBaseScale = 1.2 + sizeBonus;
    const starColor = isCenterStar ? '#22d3ee' : '#818cf8'; 
    const coreSize = isCenterStar ? 0.35 : 0.25;

    const tier = userData?.tier || userData?.connection?.tier;
    const isAcquaintance = tier === 'Acquaintance';

    const { scale } = useSpring({
        scale: isHovered ? (finalBaseScale) * 1.2 : finalBaseScale,
        config: { mass: 1, tension: 280, friction: 60 },
    });

    useFrame((state) => {
        if (ref.current) {
            const t = state.clock.getElapsedTime();
            ref.current.rotation.y = t * 0.1;
            ref.current.rotation.z = t * 0.05;
            const pulse = Math.sin(t * 2 + (userData.id.charCodeAt(0) % 10)) * 0.05;
            const currentScale = scale.get() + pulse;
            ref.current.scale.setScalar(currentScale);
        }
    });

    return (
        <group position={position}>
            <a.group ref={ref}>
                <mesh
                    onPointerOver={(e) => { e.stopPropagation(); setIsHovered(true); onHover(userData, e); }}
                    onPointerOut={() => { setIsHovered(false); onHover(null, null); }}
                    onClick={(e) => { e.stopPropagation(); onSelect(userData); }}
                    visible={false}
                >
                    <sphereGeometry args={[1.5, 16, 16]} />
                </mesh>

                {level >= 99 ? (
                    <BlackHole position={[0, 0, 0]} scale={0.25 + Math.max(0, (level - 99) * 0.0005)} />
                ) : (
                    <>
                        <mesh>
                            {isAcquaintance ? (
                                <dodecahedronGeometry args={[0.35, 0]} /> 
                            ) : (
                                <sphereGeometry args={[0.35, 64, 64]} />
                            )}
                            <meshPhysicalMaterial 
                                color={starColor}
                                emissive={starColor}
                                emissiveIntensity={10}
                                roughness={0.2}
                                metalness={0.8}
                                clearcoat={1}
                                clearcoatRoughness={0.1}
                                toneMapped={false}
                            />
                            <pointLight color={starColor} intensity={3} distance={6} decay={2} />
                        </mesh>

                        <mesh>
                            <sphereGeometry args={[0.35 * 1.4, 64, 64]} />
                            <meshPhysicalMaterial 
                                color={starColor}
                                transparent
                                opacity={0.15}
                                roughness={0}
                                metalness={0}
                                transmission={0.5}
                                thickness={2}
                                depthWrite={false}
                            />
                        </mesh>

                        <group>
                            {level >= 2 && <Moonlet scale={finalBaseScale} radius={0.6} speed={0.8} angleOffset={0} />}
                            {level >= 11 && <Moonlet scale={finalBaseScale} radius={0.75} speed={-0.6} angleOffset={Math.PI} />}
                            {level >= 26 && <CrystallineRing radius={0.95} speed={0.005} rotation={[Math.PI / 3, 0, 0]} color="#a78bfa" />}
                            {level >= 46 && <CrystallineRing radius={1.2} speed={-0.003} rotation={[-Math.PI / 4, 0, 0]} color="#f472b6" />}
                        </group>
                    </>
                )}
                

                {isSelected && !isCenterStar && (
                    <UserProfileCard profile={userData} onViewProfile={handleUserSelect} />
                )}
            </a.group>
        </group>
    );
});

const CameraController = ({ targetPosition, controlsRef }) => {
    useFrame((state, delta) => {
        if (controlsRef.current) {
            controlsRef.current.target.lerp(targetPosition, 0.05);
            controlsRef.current.update(delta);
        }
    });
    return null;
};

function InteractiveConstellation({ connections = [], interConnections = [], onSelectStar, centerUserId: propCenterUserId }) {
    const { userId, currentUserProfile, updateUserProfile } = useAppContext();
    const effectiveCenterId = propCenterUserId || userId;
    const { t } = useTranslation();
    const [hoveredProfile, setHoveredProfile] = useState(null);
    const [hoveredPosition, setHoveredPosition] = useState({ top: 0, left: 0 });
    const canvasRef = useRef();
    const starRefs = useRef(new Map());
    const controlsRef = useRef();
    const autoReturnTimer = useRef(null);
    const [selectedStarId, setSelectedStarId] = useState(null);
    const [showSettings, setShowSettings] = useState(false);

    const [constellationSettings, setConstellationSettings] = useState({
        dynamicRadiusMultiplier: 1.5,
        autoRotateSpeed: 1.7,
        starPulseIntensity: 0.05,
        bloomIntensity: 3.0,
    });

    useEffect(() => {
        if (currentUserProfile?.constellationSettings) {
            setConstellationSettings(prev => ({ ...prev, ...currentUserProfile.constellationSettings }));
        }
    }, [currentUserProfile]);

    const handleSaveSettings = async (newSettings) => {
        setConstellationSettings(newSettings);
        await updateUserProfile(userId, { constellationSettings: newSettings });
    };

    // Ensure connections is an array before iterating
    const rawConnections = Array.isArray(connections) ? connections : [];

    const safeConnections = useMemo(() => {
        return rawConnections.filter(profile => {
            // Always include the center user
            if (profile.id === effectiveCenterId) return true;
            
            // Check for Inner Circle status
            // Data structure might have tier directly on profile or inside a connection object
            const tier = profile.tier || profile.connection?.tier;
            return tier === 'Inner Circle' || tier === 'Acquaintance';
        });
    }, [rawConnections, effectiveCenterId]);
    
    const safeInterConnections = Array.isArray(interConnections) ? interConnections : [];
    
    safeConnections.forEach(profile => {
        if (!starRefs.current.has(profile.id)) {
            starRefs.current.set(profile.id, React.createRef());
        }
    });

    const numConnections = safeConnections.filter(c => c.id !== effectiveCenterId).length;
    const dynamicRadius = 4 + Math.sqrt(numConnections) * constellationSettings.dynamicRadiusMultiplier;
    const initialCameraZ = dynamicRadius * 2 + 2;

    const starPositions = useMemo(() => {
        const posMap = new Map();
        if (!safeConnections || safeConnections.length === 0) return posMap;
        const centerUser = safeConnections.find(c => c.id === effectiveCenterId);
        if (centerUser) posMap.set(centerUser.id, [0, 0, 0]);

        const innerCircleUsers = safeConnections.filter(c => {
            if (c.id === effectiveCenterId) return false;
            const tier = c.tier || c.connection?.tier;
            return tier !== 'Acquaintance'; 
        });

        const acquaintanceUsers = safeConnections.filter(c => {
            if (c.id === effectiveCenterId) return false;
            const tier = c.tier || c.connection?.tier;
            return tier === 'Acquaintance';
        });

        const distributeOnSphere = (users, radius, offset = 0) => {
            users.forEach((profile, index) => {
                const numPoints = users.length;
                if (numPoints === 0) return;
                const y = numPoints > 1 ? 1 - (index / (numPoints - 1)) * 2 : 0;
                const radiusAtY = Math.sqrt(1 - y * y);
                const phi = (index + 1) * Math.PI * (3 - Math.sqrt(5)) + offset;
                const x = Math.cos(phi) * radiusAtY;
                const z = Math.sin(phi) * radiusAtY;
                posMap.set(profile.id, [x * radius, y * radius, z * radius]);
            });
        };

        distributeOnSphere(innerCircleUsers, dynamicRadius);
        distributeOnSphere(acquaintanceUsers, dynamicRadius * 2.2, 1);

        return posMap;
    }, [safeConnections, effectiveCenterId, dynamicRadius]);

    const handleStarHover = (profile, event) => {
        setHoveredProfile(profile);
        if (profile && event && canvasRef.current) {
            const rect = canvasRef.current.getBoundingClientRect();
            setHoveredPosition({ top: event.clientY - rect.top + 15, left: event.clientX - rect.left + 15 });
        }
    };

    const CameraController = ({ targetPosition }) => {
        useFrame((state, delta) => {
            if (controlsRef.current) {
                controlsRef.current.target.lerp(targetPosition, 0.05);
                controlsRef.current.update(delta);
            }
        });
        return null;
    };

    const [cameraTarget, setCameraTarget] = useState(new THREE.Vector3(0, 0, 0));

    const handleSelectAndFocus = (profile) => {
        onSelectStar(profile);
        setSelectedStarId(profile.id === effectiveCenterId ? null : profile.id);

        const targetPos = starPositions.get(profile.id);
        if (targetPos) {
            if (profile.id === effectiveCenterId) {
                setCameraTarget(new THREE.Vector3(0, 0, 0));
            } else {
                setCameraTarget(new THREE.Vector3(...targetPos));
            }

            if (autoReturnTimer.current) clearTimeout(autoReturnTimer.current);
            autoReturnTimer.current = setTimeout(() => {
                setCameraTarget(new THREE.Vector3(0, 0, 0));
                setSelectedStarId(null);
            }, 60000);
        }
    };

    useEffect(() => () => {
        if (autoReturnTimer.current) clearTimeout(autoReturnTimer.current);
    }, []);

        return (

            <div ref={canvasRef} className="relative w-full h-[70vh] bg-black rounded-lg overflow-hidden border border-blue-900/50 shadow-glow cursor-grab active-cursor-grabbing">

                {showSettings && <ConstellationSettingsModal currentSettings={constellationSettings} onSave={handleSaveSettings} onClose={() => setShowSettings(false)} />}

                <button onClick={() => setShowSettings(true)} className="absolute top-2 right-2 z-10 ai-icon-button text-gray-300 hover:bg-white/10">

                    <LucideIcons.Settings size={20} />

                </button>

                <UserHoverCard profile={hoveredProfile} position={hoveredPosition} />

                <Canvas camera={{ position: [0, 0, initialCameraZ], fov: 60 }} dpr={[1, 1.5]} dispose={null} gl={{ toneMapping: THREE.NoToneMapping }}>

                    <React.Suspense fallback={null}>

                    <color attach="background" args={['#050508']} />

                    <Stars radius={150} depth={60} count={6000} factor={7} saturation={0} fade speed={0.5} />

                    

                    <ambientLight intensity={0.1} />

                    <pointLight position={[10, 10, 10]} intensity={1} color="#818cf8" />

    

                    <Environment preset="city" />

    

                    {/* Unified Super-Nebula - Mobile Optimized */}                <Float speed={2} rotationIntensity={0.2} floatIntensity={0.5}>
                    <StormFlash />
                    <Cloud opacity={0.85} speed={0.8} width={120} depth={20} segments={30} position={[0, 0, -5]} color="#4c1d95" /> 
                    <Cloud opacity={0.8} speed={1.2} width={60} depth={10} segments={20} position={[0, 0, 5]} color="#8b5cf6" /> 
                    <Cloud opacity={0.5} speed={1.5} width={100} depth={5} segments={15} position={[0, 0, 10]} color="#22d3ee" />
                </Float>
                
                <ShootingStar />
                <ShootingStar />
                <ShootingStar />
                <ShootingStar />
                <ShootingStar />

                <EffectComposer multisampling={0}>
                    <Bloom luminanceThreshold={0.2} luminanceSmoothing={0.8} height={300} intensity={constellationSettings.bloomIntensity} />
                    <ChromaticAberration offset={[0.0005, 0.0005]} />
                    <Vignette eskil={false} offset={0.1} darkness={1.1} />
                </EffectComposer>

                <Float speed={1} rotationIntensity={0.1} floatIntensity={0.2}>
                    {safeConnections.map((profile) => (
                        starPositions.has(profile.id) && (
                            <Star
                                ref={starRefs.current.get(profile.id)}
                                key={profile.id}
                                position={starPositions.get(profile.id)}
                                userData={profile}
                                onSelect={handleSelectAndFocus}
                                onHover={handleStarHover}
                                isCenterStar={profile.id === effectiveCenterId}
                                isSelected={profile.id === selectedStarId}
                            />
                        )
                    ))}

                    {effectiveCenterId && safeConnections.filter(c => c.id !== effectiveCenterId).map((profile) => {
                        const centerRef = starRefs.current.get(effectiveCenterId);
                        const targetRef = starRefs.current.get(profile.id);
                        if (centerRef && targetRef) {
                            const conn = profile.connection;
                            return <CosmicConnection key={`line-${profile.id}`} startRef={centerRef} endRef={targetRef} level={conn?.linkLevel || 1} isSupernova={conn?.supernova} />;
                        }
                        return null;
                    })}

                    {safeInterConnections.map(link => {
                        const sourceRef = starRefs.current.get(link.source);
                        const targetRef = starRefs.current.get(link.target);
                        if (sourceRef && targetRef) {
                            return <CosmicConnection key={`${link.source}-${link.target}`} startRef={sourceRef} endRef={targetRef} level={1} isSupernova={false} />;
                        }
                        return null;
                    })}
                </Float>

                <OrbitControls ref={controlsRef} enableZoom={true} enablePan={true} minDistance={3} maxDistance={initialCameraZ * 1.5} autoRotate autoRotateSpeed={constellationSettings.autoRotateSpeed} />
                <CameraController targetPosition={cameraTarget} controlsRef={controlsRef} />
                </React.Suspense>
            </Canvas>
        </div>
    );
}

export default InteractiveConstellation;